import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import LandingNav from '../components/shared/LandingNav';
import SiteFooter from '../components/shared/SiteFooter';
import Seo, { SITE_URL } from '../components/Seo';
import BlogArticleBody from '../components/blog/BlogArticleBody';
import BlogThumbs from '../components/blog/BlogThumbs';
import { useTheme } from '../contexts/ThemeContext';
import { AuthContext } from '../contexts/AuthContext';
import { estimateReadMinutes, fetchBlogPostBySlug, fetchBlogPosts } from '../services/blogApi';
import { fetchBlogIndexSnapshot, fetchBlogPostSnapshot } from '../services/staticContent';
import { isPrerendering } from '../utils/prerendering';
import '../components/blog/Blog.css';

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return '';
  }
}

function pickSuggested(posts, currentSlug) {
  return (posts || []).filter((p) => p.slug && p.slug !== currentSlug).slice(0, 6);
}

const BlogPost = () => {
  const { slug } = useParams();
  const { colors } = useTheme();
  const { user } = useContext(AuthContext) || {};
  const [post, setPost] = useState(null);
  const [suggestedPosts, setSuggestedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Snapshot first so the prerendered HTML contains the full article.
      const [snapshotPost, snapshotIndex] = await Promise.all([
        fetchBlogPostSnapshot(slug),
        fetchBlogIndexSnapshot(),
      ]);
      if (cancelled) return;

      const hasSnapshot = Boolean(snapshotPost?.post);
      if (hasSnapshot) {
        setPost(snapshotPost.post);
        setSuggestedPosts(pickSuggested(snapshotIndex?.posts, snapshotPost.post.slug));
        setError(null);
        setLoading(false);
      }

      if (isPrerendering() || !process.env.REACT_APP_BACKEND_URL) {
        if (!hasSnapshot) setError('Unavailable');
        setLoading(false);
        return;
      }

      try {
        if (!hasSnapshot) {
          setLoading(true);
          setError(null);
        }
        const [postData, postsData] = await Promise.all([
          fetchBlogPostBySlug(slug, { token: user?.token }),
          fetchBlogPosts({ token: user?.token }),
        ]);
        if (!cancelled) {
          setPost(postData.post);
          setSuggestedPosts(pickSuggested(postsData.posts, postData.post?.slug));
          setError(null);
        }
      } catch (err) {
        // Stale snapshot content beats an error message.
        if (!cancelled && !hasSnapshot) setError(err.message || 'Not found');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, user?.token]);

  const payload = post?.payload;
  const readMins = useMemo(() => estimateReadMinutes(payload), [payload]);
  const h1 = payload?.content?.h1 || post?.title;
  const dek = payload?.seo?.meta_description || post?.meta_description;
  const author = payload?.author;
  const schemas = useMemo(() => {
    if (!post) return null;
    const article = payload?.schema_markup?.article;
    const faq = payload?.schema_markup?.faq_page;

    // Older posts predate generated schema_markup; derive BlogPosting from the
    // fields we always have so every article ships structured data.
    const primary = article || {
      '@type': 'BlogPosting',
      headline: payload?.content?.h1 || post.title,
      description: dek || undefined,
      url: `${SITE_URL}/blog/${post.slug}`,
      mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE_URL}/blog/${post.slug}` },
      datePublished: post.published_at || post.created_at || undefined,
      dateModified: post.updated_at || post.published_at || post.created_at || undefined,
      image: post.cover_image_url || undefined,
      author: { '@type': 'Person', name: author?.name || 'Patrick' },
      publisher: {
        '@type': 'Organization',
        name: 'PrecisionTrader',
        url: SITE_URL,
        logo: { '@type': 'ImageObject', url: `${SITE_URL}/logo.png` },
      },
    };

    if (faq) return { '@context': 'https://schema.org', '@graph': [primary, faq] };
    return { '@context': 'https://schema.org', ...primary };
  }, [payload, post, dek, author]);

  const text = colors.text;
  const muted = colors.textMuted || colors.textLight || '#94a3b8';
  const isPublished = post?.status === 'published';

  return (
    <div className="blog-page" style={{ backgroundColor: colors.bgPrimary, color: text }}>
      {post && (
        <Seo
          title={payload?.seo?.title_tag || `${post.title} | PrecisionTrader`}
          description={dek}
          canonicalPath={`/blog/${post.slug}`}
          image={post.cover_image_url || undefined}
          type="article"
          noindex={!isPublished}
          schema={schemas}
          schemaId="blog-jsonld"
        />
      )}
      <LandingNav />
      <div className="blog-post-shell">
        <div className="blog-post-intro">
            <p style={{ margin: '0 0 1.5rem' }}>
              <Link to="/blog" style={{ color: muted, textDecoration: 'none', fontSize: '0.9rem' }}>
                ← Blog
              </Link>
              {user?.superuser && post && (
                <>
                  <span style={{ color: muted, margin: '0 0.5rem' }}>·</span>
                  <Link
                    to={`/blog/edit/${post.id}`}
                    style={{ color: muted, textDecoration: 'none', fontSize: '0.9rem' }}
                  >
                    Edit
                  </Link>
                </>
              )}
            </p>

            {loading && <p style={{ color: muted }}>Loading…</p>}
            {error && !loading && (
              <p style={{ color: colors.danger || '#f87171' }}>{error}</p>
            )}

            {post && !loading && (
              <>
                {!isPublished && (
                  <p
                    style={{
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      color: muted,
                      marginBottom: '1rem',
                    }}
                  >
                    {post.status?.replace('_', ' ')}
                  </p>
                )}
                {post.cover_image_url && (
                  <img
                    src={post.cover_image_url}
                    alt=""
                    style={{
                      display: 'block',
                      width: '100%',
                      height: 'auto',
                      borderRadius: '4px',
                      marginBottom: '1.75rem',
                    }}
                  />
                )}
                <h1
                  style={{
                    fontSize: 'clamp(1.85rem, 4vw, 2.6rem)',
                    fontWeight: 650,
                    lineHeight: 1.2,
                    letterSpacing: '-0.025em',
                    margin: '0 0 0.85rem',
                  }}
                >
                  {h1}
                </h1>
                {dek && (
                  <p style={{ fontSize: '1.15rem', lineHeight: 1.55, color: muted, margin: '0 0 1.25rem' }}>
                    {dek}
                  </p>
                )}
                <p style={{ fontSize: '0.9rem', color: muted, margin: '0 0 2.5rem' }}>
                  {author?.name || 'Patrick'}
                  <span style={{ margin: '0 0.4rem' }}>·</span>
                  {formatDate(post.published_at || post.created_at)}
                  <span style={{ margin: '0 0.4rem' }}>·</span>
                  {readMins} min read
                </p>
              </>
            )}
        </div>

        <div className="blog-post-body">
          {post && !loading && (
            <>
              <BlogArticleBody payload={payload} colors={colors} />

              {isPublished && (
                <BlogThumbs
                  slug={post.slug}
                  initialUp={post.thumbs_up}
                  initialDown={post.thumbs_down}
                  colors={colors}
                />
              )}
            </>
          )}
        </div>

        {suggestedPosts.length > 0 && (
          <aside className="blog-post-sidebar" aria-label="Suggested next reads">
            <p className="blog-post-sidebar-title" style={{ color: muted }}>
              Suggested Next Reads
            </p>
            <div className="blog-post-sidebar-list">
              {suggestedPosts.map((suggested) => (
                <Link
                  key={suggested.id}
                  to={`/blog/${suggested.slug}`}
                  className="blog-post-sidebar-card"
                  style={{ color: text }}
                >
                  <div className="blog-post-sidebar-card-image">
                    {suggested.cover_image_url ? (
                      <img src={suggested.cover_image_url} alt="" />
                    ) : (
                      <div className="blog-post-sidebar-card-image-empty" aria-hidden />
                    )}
                  </div>
                  <p className="blog-post-sidebar-card-title">{suggested.title}</p>
                </Link>
              ))}
            </div>
          </aside>
        )}
      </div>
      <SiteFooter />
    </div>
  );
};

export default BlogPost;
